import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { keys, read } from "~/core/query";
import { AGENT_INBOX_CHANGED, agents } from "~/modules/analytics/lib/agents";

/**
 * What the account's workflows are waiting on somebody for. The sidebar's
 * badge and the Tasks screen ask the same question under the same key, so it
 * is one request between them rather than one poll each.
 */
export function useAgentInbox(org: string, enabled = true) {
	const state = useQuery({
		queryKey: [...keys.org(org), "agent-inbox"],
		queryFn: () => agents.inbox(org),
		...read,
		// Nothing to wait on where the plan has no Agent, and a poll every ten
		// seconds for an answer that is always empty is a poll worth not making.
		enabled,
		// Every ten seconds while the tab is in view; at once when it comes back,
		// or when a response here has just changed it.
		refetchInterval: 10_000,
	});
	const { refetch } = state;
	useEffect(() => {
		const refresh = () => {
			if (!document.hidden) void refetch();
		};
		document.addEventListener("visibilitychange", refresh);
		window.addEventListener(AGENT_INBOX_CHANGED, refresh);
		return () => {
			document.removeEventListener("visibilitychange", refresh);
			window.removeEventListener(AGENT_INBOX_CHANGED, refresh);
		};
	}, [refetch]);
	return state;
}
